<script lang="ts">
  import { router, Link } from '@inertiajs/svelte';
  import momentiumLogo from '../assets/momentium-logo.png';
  import type { CalendarLayout } from '../lib/types';
  import ProfileDropdown from './ProfileDropdown.svelte';
  import NavTooltip from './NavTooltip.svelte';
  import NavDropdown from './NavDropdown.svelte';
  import FavoritesDropdown from './FavoritesDropdown.svelte';
  import FavoriteStar from './FavoriteStar.svelte';

  interface Props {
    year: number;
    layout: string;
    validLayouts: string[];
    navLabel?: string;
    todayLabel?: string;
    onPrevious: () => void;
    onNext: () => void;
    onToday: () => void;
    onLayoutChange: (layout: string) => void;
  }

  let {
    year,
    layout,
    validLayouts,
    navLabel,
    todayLabel,
    onPrevious,
    onNext,
    onToday,
    onLayoutChange,
  }: Props = $props();

  const layoutLabels: Record<string, { label: string; icon: string }> = {
    'big-year': { label: 'Big Year', icon: '📅' },
    sequential: { label: 'Sequential', icon: '📋' },
    weekday: { label: 'Weekday', icon: '📆' },
    wall: { label: 'Wall', icon: '🧱' },
    'time-blocks': { label: 'Time Blocks', icon: '🕙' },
  };

  // Tooltip state
  let navTooltipKey: string | null = $state(null);
  let navTooltipTimer: ReturnType<typeof setTimeout> | null = null;

  function showNavTooltip(key: string) {
    if (navTooltipTimer) clearTimeout(navTooltipTimer);
    navTooltipTimer = setTimeout(() => {
      navTooltipKey = key;
    }, 400);
  }

  function hideNavTooltip() {
    if (navTooltipTimer) clearTimeout(navTooltipTimer);
    navTooltipTimer = null;
    navTooltipKey = null;
  }

  // Calendar layout descriptions
  const layoutDescriptions: Record<string, { title: string; description: string }> = {
    'big-year': {
      title: 'Big Year',
      description:
        'Traditional calendar grid with months in rows. Each month shows a 6×2 mini-calendar with events as colored dots — great for spotting patterns.',
    },
    sequential: {
      title: 'Sequential',
      description:
        'All 12 months stacked vertically with days as columns. Scan through the whole year top-to-bottom with events lined up by day of month.',
    },
    weekday: {
      title: 'Weekday',
      description:
        'Months arranged by week rows. See how events fall across weekdays — useful for spotting recurring weekly patterns.',
    },
    wall: {
      title: 'Wall Calendar',
      description:
        'Classic wall-calendar layout with weeks as rows and days as columns. Familiar format, easy to read at a glance.',
    },
    'time-blocks': {
      title: 'Time Blocks',
      description:
        'Week view with Sunday-starting columns and day-part rows for timed events, plus all-day and multi-day spans at the top.',
    },
  };
</script>

<header class="header">
  <div class="header-left">
    <Link href="/" class="logo"
      ><img src={momentiumLogo} alt="Momentium" class="logo-icon" /><span class="logo-text"
        >Momentium</span
      ></Link
    >

    <NavDropdown activePage="calendar" />

    <!-- Year Navigation -->
    <div class="time-nav">
      <button class="nav-btn" onclick={onPrevious} title="Previous period">←</button>
      <button class="time-display" onclick={onToday} title="Go to current period">
        {navLabel || year}
      </button>
      <button class="nav-btn" onclick={onNext} title="Next period">→</button>
    </div>

    <button class="today-btn" onclick={onToday}>{todayLabel || 'This Year'}</button>
  </div>

  <div class="header-right">
    <!-- Favorites Dropdown -->
    <FavoritesDropdown currentPageType="calendar" currentLayout={layout as CalendarLayout} />

    <!-- Layout Toggle -->
    <div class="layout-buttons">
      {#each validLayouts as l}
        {@const info = layoutLabels[l] || { label: l, icon: '📄' }}
        {@const desc = layoutDescriptions[l]}
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="layout-btn-wrapper"
          onmouseenter={() => showNavTooltip(`layout-${l}`)}
          onmouseleave={hideNavTooltip}
        >
          <button
            class="layout-btn"
            class:active={layout === l}
            onclick={() => onLayoutChange(l)}
            title={info.label}
          >
            <span class="layout-icon">{info.icon}</span>
            <span class="layout-label">{info.label}</span>
          </button>
          {#if desc}
            <NavTooltip
              title={desc.title}
              description={desc.description}
              visible={navTooltipKey === `layout-${l}`}
            />
          {/if}
        </div>
      {/each}
    </div>

    <!-- Favorite Star (save/unsave current calendar layout) -->
    <FavoriteStar pageType="calendar" layout={layout as CalendarLayout} />

    <!-- User Profile -->
    <ProfileDropdown />
  </div>
</header>

<style>
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 1rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    gap: 1rem;
    flex-shrink: 0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  :global(.logo) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #1f2937;
    text-decoration: none;
    flex-shrink: 0;
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .time-nav {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .nav-btn {
    padding: 0.25rem 0.375rem;
    font-size: 0.75rem;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .nav-btn:hover {
    color: #1f2937;
    background: #f3f4f6;
  }

  .time-display {
    padding: 0.25rem 0.75rem;
    min-width: 4rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    text-align: center;
    transition: all 0.15s;
  }

  .time-display:hover {
    background: #f3f4f6;
  }

  .today-btn {
    padding: 0.25rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #2563eb;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .today-btn:hover {
    background: #eff6ff;
  }

  .layout-buttons {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.125rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
  }

  .layout-btn-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .layout-btn {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.1875rem 0.375rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .layout-btn:hover {
    color: #374151;
    background: rgba(255, 255, 255, 0.5);
  }

  .layout-btn.active {
    color: #1f2937;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .layout-icon {
    font-size: 0.75rem;
  }

  .layout-label {
    font-size: 0.6875rem;
  }
</style>
